import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Phone, Mail, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STORE_CONTACT } from "@/constants/contact";
import { submitContactMessage } from "@/lib/api";

interface ContactForm {
  name: string;
  email: string;
  message: string;
}

interface InfoItem {
  icon: typeof MapPin;
  label: string;
  value: string;
  href?: string;
}

const info: InfoItem[] = [
  { icon: MapPin, label: "Find us", value: STORE_CONTACT.address },
  { icon: Clock, label: "Hours", value: STORE_CONTACT.hours },
  { icon: Phone, label: "Call", value: STORE_CONTACT.phone, href: STORE_CONTACT.phoneHref },
  { icon: Mail, label: "Email", value: STORE_CONTACT.email, href: STORE_CONTACT.emailHref },
  {
    icon: Facebook,
    label: "Facebook",
    value: STORE_CONTACT.facebookHandle,
    href: STORE_CONTACT.facebookUrl,
  },
];

export const Contact = () => {
  const [form, setForm] = useState<ContactForm>({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill in your name and email.");
      return;
    }

    setSubmitting(true);
    try {
      await submitContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success("Message received. We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/15 py-4 text-base focus:outline-none focus:border-vg-cyan transition-colors placeholder:text-[#9ca3af] text-white";

  return (
    <section id="contact" className="pt-12 lg:pt-16 pb-20 lg:pb-24 bg-vg-navy text-white" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-8 overflow-hidden"
        >
          <div className="max-w-2xl shrink-0">
            <span className="font-mono-acc uppercase tracking-[0.2em] text-xs text-[#9ca3af]">04 / Contact</span>
            <h2 className="font-heading font-black tracking-tighter text-5xl md:text-7xl mt-4">
              SAY<br />
              <span className="text-vg-cyan">HELLO.</span>
            </h2>
          </div>
          <div
            aria-hidden
            className="font-heading font-black tracking-tighter text-[18vw] lg:text-[12rem] leading-none select-none text-white/5 pointer-events-none lg:text-right shrink-0"
          >
            VG MART
          </div>
        </motion.div>

        <div className="mt-12 lg:mt-14 grid lg:grid-cols-2 gap-12 lg:gap-x-16 xl:gap-x-24 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6 border-t border-white/10 pt-8"
          >
            {info.map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="flex items-start gap-5 pb-6 border-b border-white/10 last:border-b-0 last:pb-0">
                <Icon size={18} className="text-vg-cyan mt-1 shrink-0" />
                <div>
                  <div className="font-mono-acc uppercase tracking-[0.2em] text-xs text-[#9ca3af]">{label}</div>
                  {href ? (
                    <a
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="text-lg mt-1 inline-block hover:text-vg-cyan transition-colors"
                    >
                      {value}
                    </a>
                  ) : (
                    <div className="text-lg mt-1">{value}</div>
                  )}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col border-t border-white/10 pt-8"
            data-testid="contact-form"
          >
            <div className="flex-1 space-y-6">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
                disabled={submitting}
                required
                data-testid="contact-name-input"
              />
              <input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
                disabled={submitting}
                required
                data-testid="contact-email-input"
              />
              <textarea
                rows={5}
                placeholder="What's on your mind?"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${field} resize-none min-h-[140px]`}
                disabled={submitting}
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-invert mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-white text-vg-navy px-10 py-5 font-mono-acc uppercase tracking-[0.2em] text-xs hover:bg-vg-cyan hover:text-white disabled:opacity-60 disabled:pointer-events-none"
              data-testid="contact-submit-button"
            >
              {submitting ? (
                <>
                  Sending <Loader2 size={16} className="animate-spin" />
                </>
              ) : (
                <>
                  Send message <ArrowRight size={16} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
